import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Event as EventIcon,
  Save as SaveIcon,
} from '@mui/icons-material';

import {
  CalendarEventCreationProps,
  CalendarEventFormData,
  CalendarEventCreationState,
  CalendarAccount,
  MessageContent,
} from './types';
import { CalendarAccountSelector } from './CalendarAccountSelector';
import { EventForm } from './EventForm';
import { MessageContentSelector } from './MessageContentSelector';
import { getCalendarService, validateEventData, generateEventDescription } from './calendarService';

const initialFormData: CalendarEventFormData = {
  title: '',
  description: '',
  startDate: new Date().toISOString().split('T')[0],
  startTime: '09:00',
  endDate: new Date().toISOString().split('T')[0],
  endTime: '10:00',
  location: '',
  attendees: [],
  notes: '',
  selectedMessages: [],
  reminders: [{ method: 'popup', minutesBefore: 15 }],
  recurrence: { frequency: 'none' },
};

export const CalendarEventCreationDialog: React.FC<CalendarEventCreationProps> = ({
  open,
  onClose,
  onEventCreated,
  availableAccounts,
  messageContents = [],
  defaultEventData = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const calendarService = getCalendarService();

  const [state, setState] = useState<CalendarEventCreationState>({
    loading: false,
    errors: [],
    success: false,
    formData: { ...initialFormData, ...defaultEventData },
  });

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setState(prev => ({
        ...prev,
        errors: [],
        success: false,
        formData: { ...initialFormData, ...defaultEventData },
      }));
    }
  }, [open, defaultEventData]);

  const handleAccountSelect = (account: CalendarAccount) => {
    setState(prev => ({
      ...prev,
      selectedAccount: account,
      errors: [],
    }));
  };

  const handleFormDataChange = (field: keyof CalendarEventFormData, value: any) => {
    setState(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [field]: value,
      },
      errors: prev.errors.filter(error => error.field !== field),
    }));
  };

  const handleMessageToggle = (messageId: string) => {
    setState(prev => {
      const selectedMessages = prev.formData.selectedMessages.includes(messageId)
        ? prev.formData.selectedMessages.filter(id => id !== messageId)
        : [...prev.formData.selectedMessages, messageId];
      
      return {
        ...prev,
        formData: {
          ...prev.formData,
          selectedMessages,
        },
      };
    });
  };

  const validateForm = (): boolean => {
    const errors: Array<{ field: keyof CalendarEventFormData | 'selectedAccount'; message: string }> = [];

    if (!state.selectedAccount) {
      errors.push({ field: 'selectedAccount', message: 'Please select a calendar account' });
    }

    if (!state.formData.title.trim()) {
      errors.push({ field: 'title', message: 'Event title is required' });
    }

    const startDateTime = new Date(`${state.formData.startDate}T${state.formData.startTime}`);
    const endDateTime = new Date(`${state.formData.endDate}T${state.formData.endTime}`);

    if (startDateTime >= endDateTime) {
      errors.push({ field: 'endTime', message: 'End time must be after start time' });
    }

    setState(prev => ({ ...prev, errors: errors as any }));
    return errors.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setState(prev => ({ ...prev, loading: true, errors: [] }));

    try {
      const startDateTime = new Date(`${state.formData.startDate}T${state.formData.startTime}`);
      const endDateTime = new Date(`${state.formData.endDate}T${state.formData.endTime}`);

      // Get selected message contents
      const selectedMessageContents = messageContents
        .filter(msg => state.formData.selectedMessages.includes(msg.id))
        .map(msg => msg.content);

      const eventDescription = generateEventDescription(
        state.formData.description,
        selectedMessageContents
      );

      const eventData = {
        title: state.formData.title,
        description: eventDescription,
        startDateTime,
        endDateTime,
        location: state.formData.location,
        attendees: state.formData.attendees,
        notes: state.formData.notes,
        reminders: state.formData.reminders,
        recurrence: state.formData.recurrence,
      };

      const eventId = await calendarService.createEvent(state.selectedAccount!, eventData);
      
      setState(prev => ({ ...prev, success: true, loading: false }));
      
      // Call the callback with the created event data
      onEventCreated({ ...eventData, id: eventId }, state.selectedAccount!);
      
      // Close the dialog after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        errors: [{ 
          field: 'title' as keyof CalendarEventFormData, 
          message: error instanceof Error ? error.message : 'Failed to create calendar event' 
        }],
      }));
    }
  };

  const handleClose = () => {
    if (!state.loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh',
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon color="primary" />
          <Typography variant="h6" component="div">
            Create Calendar Event
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={state.loading}
          size="small"
          sx={{ ml: 1 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        {/* Success Message */}
        {state.success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Calendar event created successfully!
          </Alert>
        )}

        {/* General Error Messages */}
        {state.errors.length > 0 && state.errors.some(e => !e.field || e.field === 'selectedAccount') && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {state.errors.find(e => !e.field || e.field === 'selectedAccount')?.message || 'Please fix the errors below'}
          </Alert>
        )}

        {/* Calendar Account Selection */}
        <CalendarAccountSelector
          accounts={availableAccounts}
          selectedAccount={state.selectedAccount}
          onAccountSelect={handleAccountSelect}
          disabled={state.loading}
          error={state.errors.find(e => e.field === 'selectedAccount')?.message}
        />

        {/* Event Form */}
        <EventForm
          formData={state.formData}
          onChange={handleFormDataChange}
          errors={state.errors}
          disabled={state.loading}
        />

        {/* Message Content Selection */}
        {messageContents.length > 0 && (
          <MessageContentSelector
            messages={messageContents}
            selectedMessages={state.formData.selectedMessages}
            onMessageToggle={handleMessageToggle}
            disabled={state.loading}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end', gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={state.loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={state.loading || !state.selectedAccount}
          variant="contained"
          startIcon={state.loading ? <CircularProgress size={16} /> : <SaveIcon />}
        >
          {state.loading ? 'Creating...' : 'Create Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};